import { getCatalog, timeAgo, type Locale } from "../../i18n";
import type { PresentedEvent, PresentedNode, PresentedOrbital } from "../../presentation/mesh";
import type { NexusEdge } from "../../domain/mesh";
import { Metric, MetricGrid, RelationList, SignalList, StatusLine, type RelationItem } from "./InspectorPrimitives";

export function NodeInspectorBody({
  node,
  nodes,
  edges,
  orbitals,
  events,
  locale,
  onNode,
  onOrbital,
  onOpenSystem,
}: {
  node: PresentedNode;
  nodes: PresentedNode[];
  edges: NexusEdge[];
  orbitals: PresentedOrbital[];
  events: PresentedEvent[];
  locale: Locale;
  onNode: (id: string) => void;
  onOrbital: (nodeId: string, orbitalId: string) => void;
  onOpenSystem: (href: string) => void;
}) {
  const ui = getCatalog(locale).ui;
  const relationItems: RelationItem[] = edges.map((edge) => {
    const outgoing = edge.source === node.id;
    const peerId = outgoing ? edge.target : edge.source;
    return {
      id: edge.id,
      label: nodes.find((candidate) => candidate.id === peerId)?.label ?? peerId,
      relation: edge.relation,
      outgoing,
      onSelect: () => onNode(peerId),
    };
  });
  const componentItems: RelationItem[] = orbitals.map((orbital) => ({
    id: orbital.id,
    label: orbital.label,
    kind: orbital.kind,
    onSelect: () => onOrbital(node.id, orbital.id),
  }));
  const progress = typeof node.metrics?.progress === "number" ? node.metrics.progress : null;

  const metrics = (
    <MetricGrid>
      <Metric label={ui.lastSignal} value={timeAgo(node.metrics?.updatedAt, locale)} />
      <Metric label={ui.revision} value={node.metrics?.version ?? "—"} />
      <Metric
        label={ui.objects}
        value={
          node.metrics?.count?.toLocaleString(locale === "pl" ? "pl-PL" : locale === "de" ? "de-DE" : "en-US") ??
          relationItems.length + componentItems.length
        }
      />
      <Metric label={ui.class} value={node.metrics?.meta ?? node.cluster.toUpperCase()} />
    </MetricGrid>
  );

  const stateVector = (
    <>
      {progress !== null && (
        <section className="metric-progress">
          <div className="section-heading">
            <span>{ui.stateVector}</span>
            <b>{progress}%</b>
          </div>
          <div className="progress-track">
            <span style={{ width: `${progress}%` }} />
          </div>
        </section>
      )}
      {metrics}
    </>
  );

  return (
    <>
      <div className="catalog-kicker">
        <span>CATALOG ENTRY</span>
        <b>
          {node.kind.toUpperCase()} / {node.cluster.toUpperCase()}
        </b>
      </div>
      <StatusLine status={node.status} kind={node.kind} locale={locale} />
      <p className="inspector__description inspector__description--hero">{node.description}</p>

      <div className="inspector-desktop-detail-stack">
        {stateVector}
        {relationItems.length > 0 && (
          <section className="inspector-section">
            <div className="section-heading">
              <span>{ui.connectedEntities}</span>
              <b>{relationItems.length.toString().padStart(2, "0")}</b>
            </div>
            <RelationList items={relationItems} locale={locale} />
          </section>
        )}
        {componentItems.length > 0 && (
          <section className="inspector-section">
            <div className="section-heading">
              <span>{ui.systemComponents}</span>
              <b>{componentItems.length.toString().padStart(2, "0")}</b>
            </div>
            <RelationList items={componentItems} locale={locale} />
          </section>
        )}
        <section className="inspector-section inspector-section--events">
          <div className="section-heading">
            <span>{ui.recentSignals}</span>
            <b>{events.length.toString().padStart(2, "0")}</b>
          </div>
          <SignalList events={events} locale={locale} />
        </section>
      </div>

      {node.href && (
        <button className="open-system" type="button" onClick={() => onOpenSystem(node.href!)}>
          <span>{ui.openSystem}</span>
          <span aria-hidden="true">↗</span>
        </button>
      )}
    </>
  );
}
